"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

// Agrandit une image de l'article au clic, sans rien coûter au chargement :
// un seul écouteur posé sur le conteneur, aucune librairie, et la fenêtre
// n'existe dans le DOM qu'une fois ouverte. L'image agrandie reprend
// currentSrc, le fichier que le navigateur a déjà chargé : rien ne repart
// sur le réseau, et le clic ne déclenche qu'un petit rendu (INP).
export default function ArticleImageZoom({ children }) {
    const t = useTranslations();
    const [image, setImage] = useState(null);
    const dialogRef = useRef(null);

    const handleClick = (event) => {
        const target = event.target;
        if (target.tagName !== "IMG") return;
        // Une image dans un lien garde son lien.
        if (target.closest("a")) return;
        // Pas pendant une sélection de texte (surlignage).
        if (window.getSelection()?.toString()) return;

        setImage({
            src: target.currentSrc || target.src,
            alt: target.alt && target.alt !== "Image" ? target.alt : "",
        });
    };

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!image || !dialog) return;

        dialog.showModal();
        const root = document.documentElement;
        const previousOverflow = root.style.overflow;
        root.style.overflow = "hidden";

        return () => {
            root.style.overflow = previousOverflow;
        };
    }, [image]);

    return (
        <div
            onClick={handleClick}
            className="[&_img]:cursor-zoom-in [&_a_img]:cursor-pointer"
        >
            {children}

            {image && (
                <dialog
                    ref={dialogRef}
                    onClose={() => setImage(null)}
                    onClick={() => dialogRef.current?.close()}
                    className="m-0 h-dvh max-h-none w-screen max-w-none bg-transparent p-0 backdrop:bg-black/85"
                    aria-label={image.alt || undefined}
                >
                    <div className="flex h-full w-full cursor-zoom-out flex-col items-center justify-center gap-3 p-4 sm:p-8">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={image.src}
                            alt={image.alt}
                            className="max-h-[calc(100dvh-6rem)] max-w-full rounded-lg object-contain"
                        />
                        {image.alt && (
                            <p className="mb-0 max-w-3xl text-center text-sm text-white/80">
                                {image.alt}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => dialogRef.current?.close()}
                        aria-label={t("article_image_close")}
                        className="fixed right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        >
                            <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                    </button>
                </dialog>
            )}
        </div>
    );
}
